import React from 'react';
import { Redirect } from 'react-router-dom';
import { connect } from 'react-redux';
import { DateTime, Duration } from 'luxon';

import withStyles from '@mui/styles/withStyles';
import Paper from '@mui/material/Paper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

// import { ReOrderableItem, ReOrderableList } from "react-reorderable-list";

import { setApiResponseInfo } from '../store/slices/apiResponseInfoSlice';

import { createStdRequest, apiFetch, isAllDigits, constructPhoneRolesString, showAlert } from '../utils/utils';
import SocketAccess from '../comm/SocketAccess';
import DA from '../store/ReduxAccess';

import KDropdownMultiMenu from '../components/KDropdownMultiMenu';
import TimePicker from '../components/TimePicker';
import PhoneInput from '../components/PhoneInput';
import WebLinkEditor from '../components/WebLinkEditor';
import AlertModal from '../components/AlertModal';
import CrmTextField from '../components/CrmTextField';
import CrmButton from '../components/CrmButton';
import KDatePicker from '../components/KDatePicker';
import SearchableContactHierarchy from '../components/SearchableContactHierarchy';
import LabeledTimePicker from '../components/LabeledTimePicker';
import ColumnChooser from '../components/ColumnChooser';
import KReorderableList from '../components/KReorderableList';

const styles = (theme) => ({
  root: {
    // display: 'flex',
    marginLeft: '20px',
    marginRight: '20px',
  },
  paper: {
    marginRight: theme.spacing(2),
  },
  dateTimePanel: {
    marginTop: '24px',
    width: '300px',
  },
  fakeDatePanel: {
    display: 'inline-block',
    width: '100px',
  },
  timePanel: {
    display: 'inline-block',
    verticalAlign: 'bottom',
    width: '102px',
  },
  dateTimeComponentLabel: {
    color: '#696E6F',
    fontWeight: 500,
  },
});

const spreadsheetUploaderStyles = (theme) => ({
  root: {
    border: '1px solid black',
    width: '600px',
    backgroundColor: 'white',
    padding: '6px',
  },
  titleP: {
    fontWeight: 600,
    color: 'green',
  },
  searchHolder: {
    width: '134px',
  },
  inputRoot: {
    color: 'inherit',
    width: '100%',
  },
  inputInput: {
    border: 0,
    color: 'currentColor',
    boxSizing: 'content-box',
    background: 'none',
    paddingTop: theme.spacing(1),
    paddingRight: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(10),
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: 120,
      '&:focus': {
        width: 200,
      },
    },
  },
});

/*
const VobCarrierSearch = ({ classes, holder }) => {
  return (
    <div className={classes.searchHolder} >
        <Autocomplete
          getItemValue={(item) => item}
          shouldItemRender={shouldItemRender}
          items={carrierNames}
          renderItem={(item, isHighlighted) =>
            <div
              style={{ background: isHighlighted ? 'lightgray' : 'white' }}
              key={item}>
              {item}
            </div>
          }
          value={holder.state.insuranceCarrier}
          onSelect={(value, item) => {
            holder.setState({ insuranceCarrier: value });
          }}
          onChange={(event, value) => holder.setState({ insuranceCarrier: value })}
          wrapperProps={{className: classes.inputRoot}}
          inputProps={{placeholder: "Search...",
                       type: "text",
                       className: classes.inputInput,
                      }}
          renderMenu={renderMenu}
        />
    </div>
  );
}
*/

class SpreadsheetUploader extends React.Component {
  state = {
    running: false,
    displayingResults: false,
    file: undefined,
    result: undefined,
    customerFirmId: 42,
    customerFirmName: '',
  };

  handleFileChange(event) {
    const files = event.target.files;
    const file = files[0];
    this.setState({ file: file });
  }

  doUpload() {
    const firmId = this.state.customerFirmId.trim();
    const uri = `/api/firm/${firmId}/uploadSpreadsheetFile`;
    this.setState({ running: true, result: undefined, displayingResults: false });
    fetch(uri, { method: 'POST', body: this.state.file })
      .then((response) => response.json())
      .then((result) => {
        this.setState({ running: false, result, displayingResults: true });
        console.log('success: ' + result.success);
      })
      .catch((error) => console.log(error));
  }

  handleFirmIdChange(event) {
    const value = event.target.value;
    if (isAllDigits(value)) {
      this.setState({ customerFirmId: value });
    }
  }

  render() {
    const { classes } = this.props;
    const showUploadButton = this.state.file && !this.state.running && !this.state.displayingResults;
    return (
      <div className={classes.root}>
        <div className={classes.titleP}>Upload Spreadsheet</div>
        <div>
          Customer Firm ID:
          <input value={this.state.customerFirmId} onChange={(event) => this.handleFirmIdChange(event)} />
        </div>
        <div>
          <input type="file" accept=".ods" name="fileToUpload" onChange={(event) => this.handleFileChange(event)} />
          {showUploadButton && <input type="button" value="Upload .ods File" onClick={() => this.doUpload()} />}
        </div>
        {this.state.displayingResults && (
          <div>
            {this.state.result.success ? <span>Success!!</span> : <span>Error: {this.state.result.errorMessage}</span>}
            <table>
              <tbody>
                <tr>
                  <td>nPhones:</td>
                  <td>{this.state.result.nPhonesAddedToDb}</td>
                  <td />
                  <td>nFacilities:</td>
                  <td>{this.state.result.nFacilitiesAddedToDb}</td>
                </tr>
                <tr>
                  <td>nAddresses:</td>
                  <td>{this.state.result.nAddressesAddedToDb}</td>
                  <td />
                  <td>nFirms:</td>
                  <td>{this.state.result.nFirmsAddedToDb}</td>
                </tr>
                <tr>
                  <td>nWebLinks:</td>
                  <td>{this.state.result.nWebLinksAddedToDb}</td>
                  <td />
                  <td>nPractitioners:</td>
                  <td>{this.state.result.nPractitionersAddedToDb}</td>
                </tr>
                <tr>
                  <td>nTreatmentPrograms:</td>
                  <td>{this.state.result.nTreatmentProgramsAddedToDb}</td>
                  <td />
                  <td>nContacts:</td>
                  <td>{this.state.result.nContactsAddedToDb}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
}

class RandomUploader extends React.Component {
  state = {
    files: undefined,
  };

  handleFileChange(event) {
    const files = event.target.files;
    this.setState({ files });
  }

  doUpload() {
    const uri = `/api/document/viewingFirm/${this.props.firmId}/person/0`;
    const formData = new FormData();
    const files = this.state.files;
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    fetch(uri, { method: 'POST', body: formData })
      .then((response) => response.json())
      .then((result) => {
        console.log('success: ' + result.success);
      })
      .catch((error) => console.log(error));
  }

  render() {
    const { classes } = this.props;
    const showUploadButton = this.state.files;
    return (
      <div className={classes.root}>
        <div className={classes.titleP}>Upload Random Document</div>
        <div>
          <input type="file" onChange={(event) => this.handleFileChange(event)} />
          {showUploadButton && <input type="button" value="Upload File" onClick={() => this.doUpload()} />}
        </div>
      </div>
    );
  }
}

class Downloader extends React.Component {
  state = {
    docId: '1',
  };

  doDownload(docId) {
    const uri = `/api/document/${docId}`;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      const fileName = json.publicName;
      this.doActualDownload(docId, fileName);
    };
    apiFetch(null, request, actionOnGoodResponse);
  }

  doActualDownload(shownInt, fileName) {
    const uri = `/api/document/${shownInt}/content`;
    fetch(uri).then((response) => {
      response.blob().then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        a.click();
      });
    });
  }

  handleDocumentIdChange(event) {
    const value = event.target.value;
    if (isAllDigits(value)) {
      this.setState({ docId: value });
    }
  }

  render() {
    const { classes } = this.props;
    const shownInt = parseInt(this.state.docId);
    const showDownloadButton = shownInt && shownInt > 0;

    return (
      <div className={classes.root}>
        <div className={classes.titleP}>Download Specific Document</div>
        <div>
          Document ID:{' '}
          <input
            value={this.state.docId}
            onChange={(event) => this.handleDocumentIdChange(event)}
            style={{ width: '72px', textAlign: 'right' }}
          />
          {showDownloadButton ? (
            <input
              type="button"
              value="Download Document"
              onClick={() => this.doDownload(this.state.docId)}
              style={{ marginLeft: '10px' }}
            />
          ) : null}
        </div>
      </div>
    );
  }
}

const SpreadsheetUploaderStyled = withStyles(spreadsheetUploaderStyles)(SpreadsheetUploader);
const RandomUploaderStyled = withStyles(spreadsheetUploaderStyles)(RandomUploader);
const DownloaderStyled = withStyles(spreadsheetUploaderStyles)(Downloader);

class AllevaPlayPage extends React.Component {
  state = {
    redirect: null,
    contactPhone1Roles: 'Mobile',
    contactPhone1Number: '',
    includeProspects: true,
    includeClients: true,
    includeAlumni: true,
    nIds: 0,
    timeForIndividualCalls: 0,
    lengthOfIndividualResponse: 0,
    timeForCollectiveCall: 0,
    lengthOfCollectiveResponse: 0,
    timeForCollectiveCall2: 0,
    lengthOfCollectiveResponse2: 0,
    queryString: '',
    timeText: '9:04 AM',
    timeDuration: Duration.fromObject({ hours: 13, minutes: 13 }),
    syncArgs: {
      firmId: 0,
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
    },
    contactSelectionData: {
      categories: [],
      businesses: [],
      contacts: [],
      specials: [],
    },
    chosenContactCategory: null,
    chosenContactBusiness: null,
    chosenContactContact: null,
    shouldShowBusinessDropdown: true,
    shouldShowContactDropdown: true,
    // controlledDate: DateTime.local(),
    controlledDate: null,
  };

  shouldShowAlertModalButton = false;
  shouldShowWebSocketsButtons = false;
  shouldShowSyncablePersonFinder = false;
  shouldShowWebLinkEditor = false;
  shouldShowSimpleAutocomplete = false;
  shouldShowSearchableContactsExperiment = false;
  shouldShowSearchableContactsComponent = false;
  shouldShowSpreadsheetUploader = false;
  shouldShowRandomUploader = false;
  shouldShowDownloader = false;
  shouldShowPhoneInput = false;
  shouldShowSmsTester = false;
  shouldShowTimePicker = false;
  shouldShowTimePicker2 = false;
  shouldShowDatePicker2 = true;
  shouldShowMultiMenu = false;
  shouldShowGetIdsTest = false;
  shouldShowGetIndividualCallsTest = false;
  shouldShowGetCollectiveCallsTest = false;
  shouldShowGetQueryStringTest = false;
  shouldShowGetCollectiveQueryStringTest = false;
  shouldShowColumnChooser = false;
  shouldShowPlainTextField = true;
  shouldShowReorderableListTest = true;

  shouldParseAndBuildFromRealData = true;

  /*
   * 2143,Samuel,Coleridge,,allevaClient01+jiMNfIYL7p@gmail.com
   * 2143,Norma,Mortensen,2136443001,NormaJean+jiMNfIYL7p@gmail.com
   */

  allPossiblePhoneRoles = ['Mobile', 'Business', 'Home', 'Work', 'Fax'];
  possibleContactPhoneRoles = ['Work', 'Mobile', 'Home'];

  syntheticSearchableContactData = this.createSyntheticData();

  componentDidMount() {
    if (this.shouldShowSearchableContactsExperiment) {
      this.populateSearchableContactsDropdowns();
    }
  }

  handleContactPhone1RolesChange(rolesObj) {
    const newPhoneRoles = constructPhoneRolesString(rolesObj, this.allPossiblePhoneRoles);
    this.setState({ contactPhone1Roles: newPhoneRoles });
  }

  handleContactPhone1NumberChange(newNumber) {
    this.setState({ contactPhone1Number: newNumber });
  }

  handleFilterChange = (stateVariableName) => (event) => {
    this.setState({ [stateVariableName]: event.target.checked });
  };

  items = [
    {
      label: 'Prospects',
      checked: this.state.includeProspects,
      name: 'includeProspects',
      toggleFunc: this.handleFilterChange('includeProspects'),
    },
    {
      label: 'Current Clients',
      checked: this.state.includeClients,
      name: 'includeClients',
      toggleFunc: this.handleFilterChange('includeClients'),
    },
    {
      label: 'Alumni',
      checked: this.state.includeAlumni,
      name: 'includeAlumni',
      toggleFunc: this.handleFilterChange('includeAlumni'),
    },
  ];

  getIds() {
    const uri = '/api/person/ids';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      const nIds = json.length;
      this.setState({ nIds: nIds });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  countOfIndividualCalls = 0;
  individualCallStartTime = DateTime.local();

  makeIndividualCalls() {
    this.countOfIndividualCalls = 0;
    const uri = '/api/person/ids';
    const request = createStdRequest(uri);
    const callback = () => {
      const timeForIndividualCalls = DateTime.local().minus(this.individualCallStartTime).as('milliseconds');
      const lengthOfIndividualResponse = this.countOfIndividualCalls;
      this.setState({ lengthOfIndividualResponse, timeForIndividualCalls });
    };
    const actionOnGoodResponse = (json) => {
      this.individualCallStartTime = DateTime.local();
      this.recursivelyMakeIndividualCalls(json, callback);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  recursivelyMakeIndividualCalls(listOfPersonIds, callbackWhenDone) {
    if (listOfPersonIds.length === 0) {
      callbackWhenDone();
    } else {
      const head = listOfPersonIds[0];
      const tail = listOfPersonIds.slice(1);
      const uri = '/api/person/' + head;
      const request = createStdRequest(uri);
      const actionOnGoodResponse = (json) => {
        this.countOfIndividualCalls++;
        this.recursivelyMakeIndividualCalls(tail, callbackWhenDone);
      };
      apiFetch(this, request, actionOnGoodResponse);
    }
  }

  makeCollectiveCall() {
    const uri = '/api/person';
    const request = createStdRequest(uri);
    const startTime = DateTime.local();
    const actionOnGoodResponse = (json) => {
      const timeForCollectiveCall = DateTime.local().minus(startTime).as('milliseconds');
      const lengthOfCollectiveResponse = json.length;
      this.setState({ timeForCollectiveCall, lengthOfCollectiveResponse });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  makeCollectiveCall2() {
    const uri = '/api/person/ids';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      // const idsToQuery = json.slice(0, 40);
      const idsToQuery = json;
      const queryStringParam = idsToQuery.join('+');
      console.log(queryStringParam);
      const innerUri = '/api/person/forIds/?ids=' + queryStringParam;
      const innerRequest = createStdRequest(innerUri);
      const startTime = DateTime.local();
      const innerActionOnGoodResponse = (responseJson) => {
        const timeForCollectiveCall2 = DateTime.local().minus(startTime).as('milliseconds');
        const lengthOfCollectiveResponse2 = responseJson.length;
        this.setState({ timeForCollectiveCall2, lengthOfCollectiveResponse2 });
      };
      apiFetch(this, innerRequest, innerActionOnGoodResponse);
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  testQueryString() {
    const uri = '/api/person/testQueryString?ids=123+456+789';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      console.log(json);
      this.setState({ queryString: json.queryString });
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  fakeHandleIncomingSms() {
    const uri = '/api/demo/fakeIncomingSms';
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      showAlert(this, 'finished with incoming sms');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  handleFileChange(event) {
    console.log('value: ' + event.target.value);
    const files = event.target.files;
    const file = files[0];
    console.log('file: ' + file);
    const firmId = 42;
    const uri = `/api/firm/${firmId}/uploadSpreadsheetFile`;
    fetch(uri, { method: 'POST', body: file })
      .then((response) => response.json())
      .then((success) => {
        // Do something with the successful response
        console.log('success: ' + success);
      })
      .catch((error) => console.log(error));
  }

  findSyncablePerson() {
    const { firmId, firstName, lastName, phoneNumber, email } = this.state.syncArgs;
    let queryString = `?firmId=${firmId}`;
    queryString += `&firstName=${firstName}`;
    queryString += `&lastName=${lastName}`;
    queryString += `&phone=${encodeURIComponent(phoneNumber)}`;
    queryString += `&email=${email}`;
    const uri = '/api/sync/findPerson' + queryString;
    const request = createStdRequest(uri);
    const actionOnGoodResponse = (json) => {
      showAlert(this, 'finished with findSyncablePerson');
    };
    apiFetch(this, request, actionOnGoodResponse);
  }

  populateSearchableContactsDropdowns() {
    this.setState({ contactSelectionData: this.syntheticSearchableContactData });
  }

  createSyntheticData() {
    const quasiCustomData = this.createAccountSpecificData();
    const fullData = this.augmentAccountSpecificDataWithStandardData(quasiCustomData);
    return fullData;
  }

  createAccountSpecificData() {
    if (this.shouldParseAndBuildFromRealData) {
      return this.parseAndBuildFromRealData();
    } else {
      return this.createPreFabAccountSpecificData();
    }
  }

  parseAndBuildFromRealData() {
    // const knownFirms = DA.getAllKnownFirms();
    const knownFirms = JSON.parse(knownFirmsJson).knownFirms;

    // First collect all the categories
    const categoryNamesArray = knownFirms.map((firm) => firm.firmType);
    const categoryNamesSet = new Set(categoryNamesArray);
    const orderedCategoryNames = Array.from(categoryNamesSet).sort();
    const categories = orderedCategoryNames.map((name) => {
      return { id: 'Cat:' + name, name: name };
    });

    // Now, run through firms adding to both businesses and contacts
    let businesses = [];
    let contacts = [];
    knownFirms.forEach((firm) => {
      const categoryName = firm.firmType;
      const categoryObj = categories.find((catObj) => catObj.name === categoryName);
      const categoryId = categoryObj.id;
      const businessId = categoryId + ':Biz:' + firm.id;
      const businessObj = {
        id: businessId,
        categoryId,
        name: firm.name,
      };
      businesses = [...businesses, businessObj];
      const contactsFromRedux = firm.contacts;
      contactsFromRedux.forEach((contact) => {
        const contactObj = {
          businessId,
          name: contact.name,
          personId: contact.id,
        };
        contacts = [...contacts, contactObj];
      });
    });

    businesses = businesses.sort((b1, b2) => b1.name.localeCompare(b2.name));
    contacts = contacts.sort((c1, c2) => c1.name.localeCompare(c2.name));

    return { categories, businesses, contacts };
  }

  createPreFabAccountSpecificData() {
    return {
      categories: [
        {
          id: 'Cat:Hospital',
          name: 'Hospital',
        },
        {
          id: 'Cat:TreatmntCtr',
          name: 'Treatmnt Ctr',
        },
        {
          id: 'Cat:EdConsultant',
          name: 'Ed Consultant',
        },
        {
          id: 'Cat:Practitioner',
          name: 'Practitioner',
        },
      ],
      businesses: [
        {
          id: 'Cat:Hospital:Biz:Biz00',
          categoryId: 'Cat:Hospital',
          name: 'Biz00 of type Hospital',
        },
        {
          id: 'Cat:Hospital:Biz:Biz01',
          categoryId: 'Cat:Hospital',
          name: 'Biz01 of type Hospital',
        },
        {
          id: 'Cat:TreatmntCtr:Biz:Biz00',
          categoryId: 'Cat:TreatmntCtr',
          name: 'Biz00 of type Treatmnt Ctr',
        },
        {
          id: 'Cat:TreatmntCtr:Biz:Biz01',
          categoryId: 'Cat:TreatmntCtr',
          name: 'Biz01 of type Treatmnt Ctr',
        },
        {
          id: 'Cat:EdConsultant:Biz:Biz00',
          categoryId: 'Cat:EdConsultant',
          name: 'Biz00 of type Ed Consultant',
        },
        {
          id: 'Cat:EdConsultant:Biz:Biz01',
          categoryId: 'Cat:EdConsultant',
          name: 'Biz01 of type Ed Consultant',
        },
        {
          id: 'Cat:Practitioner:Biz:Biz00',
          categoryId: 'Cat:Practitioner',
          name: 'Biz00 of type Practitioner',
        },
        {
          id: 'Cat:Practitioner:Biz:Biz01',
          categoryId: 'Cat:Practitioner',
          name: 'Biz01 of type Practitioner',
        },
      ],
      contacts: [
        {
          businessId: 'Cat:Hospital:Biz:Biz00',
          name: 'Contact00 of Biz00 of type Hospital',
          personId: 1,
        },
        {
          businessId: 'Cat:Hospital:Biz:Biz00',
          name: 'Contact01 of Biz00 of type Hospital',
          personId: 2,
        },
        {
          businessId: 'Cat:Hospital:Biz:Biz01',
          name: 'Contact00 of Biz01 of type Hospital',
          personId: 3,
        },
        {
          businessId: 'Cat:Hospital:Biz:Biz01',
          name: 'Contact01 of Biz01 of type Hospital',
          personId: 4,
        },
        {
          businessId: 'Cat:TreatmntCtr:Biz:Biz00',
          name: 'Contact00 of Biz00 of type Treatmnt Ctr',
          personId: 5,
        },
        {
          businessId: 'Cat:TreatmntCtr:Biz:Biz00',
          name: 'Contact01 of Biz00 of type Treatmnt Ctr',
          personId: 6,
        },
        {
          businessId: 'Cat:TreatmntCtr:Biz:Biz01',
          name: 'Contact00 of Biz01 of type Treatmnt Ctr',
          personId: 7,
        },
        {
          businessId: 'Cat:TreatmntCtr:Biz:Biz01',
          name: 'Contact01 of Biz01 of type Treatmnt Ctr',
          personId: 8,
        },
        {
          businessId: 'Cat:EdConsultant:Biz:Biz00',
          name: 'Contact00 of Biz00 of type Ed Consultant',
          personId: 9,
        },
        {
          businessId: 'Cat:EdConsultant:Biz:Biz00',
          name: 'Contact01 of Biz00 of type Ed Consultant',
          personId: 10,
        },
        {
          businessId: 'Cat:EdConsultant:Biz:Biz01',
          name: 'Contact00 of Biz01 of type Ed Consultant',
          personId: 11,
        },
        {
          businessId: 'Cat:EdConsultant:Biz:Biz01',
          name: 'Contact01 of Biz01 of type Ed Consultant',
          personId: 12,
        },
        {
          businessId: 'Cat:Practitioner:Biz:Biz00',
          name: 'Contact00 of Biz00 of type Practitioner',
          personId: 13,
        },
        {
          businessId: 'Cat:Practitioner:Biz:Biz00',
          name: 'Contact01 of Biz00 of type Practitioner',
          personId: 14,
        },
        {
          businessId: 'Cat:Practitioner:Biz:Biz01',
          name: 'Contact00 of Biz01 of type Practitioner',
          personId: 15,
        },
        {
          businessId: 'Cat:Practitioner:Biz:Biz01',
          name: 'Contact01 of Biz01 of type Treatmnt Ctr',
          personId: 16,
        },
      ],
    };
  }

  augmentAccountSpecificDataWithStandardData(accountData) {
    let categories = accountData.categories;
    let businesses = accountData.businesses;
    const contacts = accountData.contacts;

    const standardCategories = [
      {
        id: 'Cat:Relative',
        name: 'Relative',
        special: true,
        nKids: 1,
        kidLabel: 'Relationship to Prospect',
      },
      {
        id: 'Cat:Acquaintance',
        name: 'Acquaintance',
        special: true,
        nKids: 1,
        kidLabel: 'Relationship to Prospect',
      },
      {
        id: 'Cat:Self',
        name: 'Self',
        special: true,
        nKids: 0,
      },
    ];

    const standardBusinesses = [
      {
        id: 'Cat:Relative:Name:Parent',
        categoryId: 'Cat:Relative',
        name: 'Parent',
        terminal: true,
      },
      {
        id: 'Cat:Relative:Name:Spouse',
        categoryId: 'Cat:Relative',
        name: 'Spouse',
        terminal: true,
      },
      {
        id: 'Cat:Relative:Name:Partner',
        categoryId: 'Cat:Relative',
        name: 'Partner',
        terminal: true,
      },
      {
        id: 'Cat:Relative:Name:Other',
        categoryId: 'Cat:Relative',
        name: 'Other Relative',
        terminal: true,
      },
      {
        id: 'Cat:Acquaintance:Name:OthFriender',
        categoryId: 'Cat:Acquaintance',
        name: 'Friend',
        terminal: true,
      },
      {
        id: 'Cat:Acquaintance:Name:Coworker',
        categoryId: 'Cat:Acquaintance',
        name: 'Coworker',
        terminal: true,
      },
      {
        id: 'Cat:Acquaintance:Name:Minister',
        categoryId: 'Cat:Acquaintance',
        name: 'Minister',
        terminal: true,
      },
      {
        id: 'Cat:Acquaintance:Name:Other',
        categoryId: 'Cat:Acquaintance',
        name: 'Other Acquaintance',
        terminal: true,
      },
    ];

    categories = [...categories, ...standardCategories];
    businesses = [...businesses, ...standardBusinesses];

    return { categories, businesses, contacts };
  }

  showAlertModal() {
    showAlert(this, 'OK, this is your alert!');
  }

  testWebSockets(shouldBroadcast) {
    const randInt = Math.floor(Math.random() * 10000);
    const msg = 'Test with random number: ' + randInt;
    SocketAccess.sendToSocket(msg, shouldBroadcast);
  }

  computeContactChooserOptions() {
    const { chosenContactCategory, chosenContactBusiness, chosenContactContact } = this.state;

    const selectionState = chosenContactCategory
      ? chosenContactBusiness
        ? chosenContactContact
          ? /* Row 8*/ 'All Chosen'
          : /* Row 7*/ 'Category and Business'
        : chosenContactContact
        ? /* Row 6*/ 'Category and Contact'
        : /* Row 5*/ 'Just Category'
      : chosenContactBusiness
      ? chosenContactContact
        ? /* Row 4*/ 'Business and Contact'
        : /* Row 3*/ 'Just Business'
      : chosenContactContact
      ? /* Row 2*/ 'Just Contact'
      : /* Row 1*/ 'None Chosen';

    // console.log(`selectionState: ${selectionState}`);

    const data = this.state.contactSelectionData;

    const contactCategoryOptions = data.categories;
    let contactBusinessOptions = data.businesses;
    let contactOptions = data.contacts;

    switch (selectionState) {
      case 'None Chosen':
        // already have everything showing, which is what we want
        break;
      case 'Just Contact':
        // already have everything showing, which is what we want
        break;
      case 'Just Business':
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      case 'Business and Contact':
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      case 'Just Category':
        contactBusinessOptions = this.getBusinessOptionsForCategoryOption(chosenContactCategory);
        contactOptions = this.getContactOptionsForBusinessOptions(contactBusinessOptions);
        break;
      case 'Category and Contact':
        contactBusinessOptions = this.getBusinessOptionsForCategoryOption(chosenContactCategory);
        contactOptions = this.getContactOptionsForBusinessOptions(contactBusinessOptions);
        break;
      case 'Category and Business':
        contactBusinessOptions = this.getBusinessOptionsForCategoryOption(chosenContactCategory);
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      case 'All Chosen':
        contactBusinessOptions = this.getBusinessOptionsForCategoryOption(chosenContactCategory);
        contactOptions = this.getContactOptionsForBusinessOption(chosenContactBusiness);
        break;
      default:
        break;
    }

    return { contactCategoryOptions, contactBusinessOptions, contactOptions };
  }

  getBusinessOptionsForCategoryOption(categoryOption) {
    return this.state.contactSelectionData.businesses.filter((bizObj) => bizObj.categoryId === categoryOption.id);
  }

  getContactOptionsForBusinessOption(businessOption) {
    return this.state.contactSelectionData.contacts.filter((contactObj) => contactObj.businessId === businessOption.id);
  }

  getContactOptionsForBusinessOptions(businessOptions) {
    let contactOptions = [];
    businessOptions.forEach((businessOption) => {
      const contactOptionsForThisBusiness = this.getContactOptionsForBusinessOption(businessOption);
      contactOptions = [...contactOptions, ...contactOptionsForThisBusiness];
    });
    return contactOptions;
  }

  setChosenContactCategory(chosenCategoryOption) {
    if (chosenCategoryOption) {
      console.log(`setting chosenContactCategory: *${chosenCategoryOption.name}*`);
      console.log('clearing chosenContactBusiness and chosenContactContact');
      this.setState({
        chosenContactCategory: chosenCategoryOption,
        chosenContactBusiness: null,
        chosenContactContact: null,
      });
      if (chosenCategoryOption.special) {
        const nKids = chosenCategoryOption.nKids;
        console.log(`nKids: ${nKids}`);
        if (nKids < 2) {
          this.setState({ shouldShowContactDropdown: false });
        }
        if (nKids < 1) {
          this.setState({ shouldShowBusinessDropdown: false });
        }
      } else {
        this.setState({ shouldShowContactDropdown: true, shouldShowBusinessDropdown: true });
      }
    } else {
      console.log(`clearing chosenContactCategory, chosenContactBusiness, and chosenContactContact`);
      this.setState({
        chosenContactCategory: null,
        chosenContactBusiness: null,
        chosenContactContact: null,
        shouldShowBusinessDropdown: true,
        shouldShowContactDropdown: true,
      });
    }
  }

  setChosenContactBusiness(chosenBusinessOption) {
    if (chosenBusinessOption) {
      const categoryOption = this.getCategoryOptionForBusinessOption(chosenBusinessOption);
      console.log(`setting chosenContactCategory: *${categoryOption.name}*`);
      console.log(`setting chosenContactBusiness: *${chosenBusinessOption.name}*`);
      console.log('clearing chosenContactContact');
      this.setState({
        chosenContactCategory: categoryOption,
        chosenContactBusiness: chosenBusinessOption,
        chosenContactContact: null,
      });
    } else {
      console.log(`clearing chosenContactBusiness, and chosenContactContact`);
      this.setState({ chosenContactBusiness: '', chosenContactContact: null });
    }
  }

  getCategoryOptionForBusinessOption(businessOption) {
    const categoryOptions = this.state.contactSelectionData.categories;
    return categoryOptions.find((categoryObj) => categoryObj.id === businessOption.categoryId);
  }

  setChosenContactOption(contactOption) {
    if (contactOption) {
      const businessOption = this.getBusinessOptionForContactOption(contactOption);
      const categoryOption = this.getCategoryOptionForBusinessOption(businessOption);
      console.log(`setting chosenContactCategory: *${categoryOption.name}*`);
      console.log(`setting chosenContactBusiness: *${businessOption.name}*`);
      console.log(`setting chosenContactContact: *${contactOption.name}*`);
      this.setState({
        chosenContactCategory: categoryOption,
        chosenContactBusiness: businessOption,
        chosenContactContact: contactOption,
      });
    } else {
      console.log(`clearing chosenContactContact`);
      this.setState({ chosenContactContact: null });
    }
  }

  getBusinessOptionForContactOption(contactOption) {
    const businessOptions = this.state.contactSelectionData.businesses;
    return businessOptions.find((bizObj) => bizObj.id === contactOption.businessId);
  }

  handleNewDate(newDate) {
    console.log(`newDate: ${newDate.toISO()}`);
    this.setState({ controlledDate: newDate });
  }

  render() {
    if (this.state.redirect) {
      return <Redirect to={this.state.redirect} />;
    }

    const { classes, userInfo } = this.props;
    const items = [
      {
        label: 'Prospects',
        checked: this.state.includeProspects,
        name: 'includeProspects',
        toggleFunc: this.handleFilterChange('includeProspects'),
      },
      {
        label: 'Current Clients',
        checked: this.state.includeClients,
        name: 'includeClients',
        toggleFunc: this.handleFilterChange('includeClients'),
      },
      {
        label: 'Alumni',
        checked: this.state.includeAlumni,
        name: 'includeAlumni',
        toggleFunc: this.handleFilterChange('includeAlumni'),
      },
    ];
    const autoOptions = ['12345', '23456', '23789', '23123', '00000'];

    const { contactCategoryOptions, contactBusinessOptions, contactOptions } = this.computeContactChooserOptions();
    const { chosenContactCategory, chosenContactBusiness, chosenContactContact } = this.state;
    const companyDropdownLabel = chosenContactCategory
      ? chosenContactCategory.kidLabel || 'Referral Company'
      : 'Referral Company';

    return (
      <div className={classes.root}>
        <AlertModal renderer={this} show={this.state.shouldShowAlertModal} text={this.state.alertModalText} />
        {userInfo.prefName === 'Kim' && (
          <button onClick={() => this.setState({ redirect: 'kimsDangerousPage' })}>
            Go to Kim's special Experimentation Page
          </button>
        )}
        {this.shouldShowAlertModalButton && (
          <div>
            <button onClick={() => this.showAlertModal()}>Show Alert Modal</button>
          </div>
        )}

        {this.shouldShowSearchableContactsExperiment && (
          <div style={{ marginTop: '20px' }}>
            <div style={{ display: 'inline-block', margin: '20px' }}>
              <Autocomplete
                options={contactCategoryOptions}
                getOptionLabel={(option) => option.name}
                value={chosenContactCategory}
                style={{ width: 300 }}
                renderInput={(params) => <TextField {...params} label="Referral Source Type" variant="outlined" />}
                onChange={(event, value) => this.setChosenContactCategory(value)}
                onClose={() => console.log('got onClose')}
                onInputChange={() => console.log('got onInputChange')}
                onOpen={() => console.log('got onOpen')}
              />
            </div>
            {this.state.shouldShowBusinessDropdown && (
              <div style={{ display: 'inline-block', margin: '20px' }}>
                <Autocomplete
                  options={contactBusinessOptions}
                  getOptionLabel={(option) => option.name}
                  value={chosenContactBusiness}
                  style={{ width: 300 }}
                  renderInput={(params) => <TextField {...params} label={companyDropdownLabel} variant="outlined" />}
                  onChange={(event, value) => {
                    this.setChosenContactBusiness(value);
                  }}
                  onClose={() => console.log('got onClose')}
                  onInputChange={() => console.log('got onInputChange')}
                  onOpen={() => console.log('got onOpen')}
                />
              </div>
            )}
            {this.state.shouldShowContactDropdown && (
              <div style={{ display: 'inline-block', margin: '20px' }}>
                <Autocomplete
                  options={contactOptions}
                  getOptionLabel={(option) => option.name}
                  value={chosenContactContact}
                  style={{ width: 400 }}
                  renderInput={(params) => <TextField {...params} label="Referral Contact" variant="outlined" />}
                  onChange={(event, value) => this.setChosenContactOption(value)}
                  onClose={() => console.log('got onClose')}
                  onInputChange={() => console.log('got onInputChange')}
                  onOpen={() => console.log('got onOpen')}
                />
              </div>
            )}
          </div>
        )}

        {this.shouldShowSearchableContactsComponent && (
          <SearchableContactHierarchy
            data={this.syntheticSearchableContactData}
            newOrgTypeFunc={() => alert('Should show Modal to define a new Category')}
            newCompanyFunc={() => alert('Should show Modal to define a new Referral Company')}
            newReferralContactFunc={() => alert('Should show Modal to define a new Referral Contact')}
            defineRelativeFunc={(name) => alert('Should show Modal to define a ' + name)}
            defineAcquaintanceFunc={(name) => alert('Should show Modal to define a ' + name)}
            designateContactFunc={(personId) =>
              alert('Should call some function on my page to designate the chosen contact with ID: ' + personId)
            }
            selfReferralFunc={() => alert('Should call some function to register that this is a self-referrral')}
          />
        )}

        {this.shouldShowWebSocketsButtons && (
          <div>
            <button onClick={() => this.testWebSockets(false)}>WebSockets Echo</button>
            <button onClick={() => this.testWebSockets(true)}>WebSockets Broadcast</button>
          </div>
        )}

        {this.shouldShowSyncablePersonFinder && (
          <div style={{ width: '200px', border: '1px solid blue' }}>
            <CrmTextField
              fullWidth
              label="Firm ID"
              value={this.state.syncArgs.firmId}
              onChange={(event) => this.setState({ syncArgs: { ...this.state.syncArgs, firmId: event.target.value } })}
            />
            <CrmTextField
              fullWidth
              label="First Name"
              value={this.state.syncArgs.firstName}
              onChange={(event) =>
                this.setState({ syncArgs: { ...this.state.syncArgs, firstName: event.target.value } })
              }
            />
            <CrmTextField
              fullWidth
              label="Last Name"
              value={this.state.syncArgs.lastName}
              onChange={(event) =>
                this.setState({ syncArgs: { ...this.state.syncArgs, lastName: event.target.value } })
              }
            />
            <CrmTextField
              fullWidth
              label="Phone Number"
              value={this.state.syncArgs.phoneNumber}
              onChange={(event) =>
                this.setState({ syncArgs: { ...this.state.syncArgs, phoneNumber: event.target.value } })
              }
            />
            <CrmTextField
              fullWidth
              label="Email"
              value={this.state.syncArgs.email}
              onChange={(event) => this.setState({ syncArgs: { ...this.state.syncArgs, email: event.target.value } })}
            />
            <CrmButton filled onClick={() => this.findSyncablePerson()}>
              Find
            </CrmButton>
          </div>
        )}

        {this.shouldShowWebLinkEditor && (
          <div>
            <WebLinkEditor
              label="Website Link"
              linkBoxWidth="500px"
              initialValue="http://abc.com"
              clickawayShouleSave={false}
              onDelete={() => showAlert(this, 'should delete link now')}
              onNewValue={(newText) => showAlert(this, 'should save link now')}
            />
          </div>
        )}

        {this.shouldShowSimpleAutocomplete && (
          <div>
            <Autocomplete
              options={autoOptions}
              getOptionLabel={(option) => option}
              style={{ width: 300 }}
              renderInput={(params) => <TextField {...params} label="Search" variant="outlined" />}
              onChange={() => console.log('got onChange')}
              onClose={() => console.log('got onClose')}
              onInputChange={() => console.log('got onInputChange')}
              onOpen={() => console.log('got onOpen')}
            />
          </div>
        )}

        {this.shouldShowSpreadsheetUploader && (
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <SpreadsheetUploaderStyled />
          </div>
        )}

        {this.shouldShowRandomUploader && (
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <RandomUploaderStyled firmId={DA.getFirmId()} />
          </div>
        )}

        {this.shouldShowDownloader && (
          <div style={{ marginTop: '24px', marginBottom: '24px' }}>
            <DownloaderStyled />
          </div>
        )}

        {this.shouldShowPhoneInput && (
          <div style={{ marginLeft: '100px', marginBottom: '40px', background: 'white' }}>
            <PhoneInput
              label="Phone 1"
              possibleRoles={this.possibleContactPhoneRoles}
              actualRoles={this.state.contactPhone1Roles}
              number={this.state.contactPhone1Number}
              onRolesChange={(rolesObj) => this.handleContactPhone1RolesChange(rolesObj)}
              onNumberChange={(newNumber) => this.handleContactPhone1NumberChange(newNumber)}
            />
          </div>
        )}

        {this.shouldShowSmsTester && (
          <div>
            <button onClick={() => this.fakeHandleIncomingSms()}>Fake Handle Incoming SMS</button>
          </div>
        )}

        {this.shouldShowTimePicker && (
          <div className={classes.dateTimePanel}>
            <div className={classes.fakeDatePanel} onClick={() => showAlert(this, 'got click')}>
              Date
            </div>
            <div className={classes.timePanel}>
              <div className={classes.dateTimeComponentLabel}>Date</div>
              <TimePicker
                value={this.state.timeText}
                onChange={(newTimeText) => this.setState({ timeText: newTimeText })}
              />
            </div>
          </div>
        )}

        {this.shouldShowTimePicker2 && (
          <div className={classes.dateTimePanel}>
            <div className={classes.timePanel}>
              <LabeledTimePicker
                label="My Time"
                value={this.state.timeDuration}
                onChange={(newTimeDuration) => this.setState({ timeDuration: newTimeDuration })}
              />
            </div>
          </div>
        )}

        {this.shouldShowMultiMenu && (
          <div className={classes.root}>
            <Paper className={classes.paper}>
              <LocalMenuList items={items} />
            </Paper>
            <KDropdownMultiMenu classes={classes} label="Filter" items={items} />
          </div>
        )}

        {this.shouldShowGetIdsTest && (
          <div style={{ margin: '30px 12px' }}>
            <button onClick={() => this.getIds()}>Get Ids</button>
            <span style={{ marginLeft: '10px' }}>{this.state.nIds + ' '} ids</span>
          </div>
        )}

        {this.shouldShowGetIndividualCallsTest && (
          <div style={{ margin: '30px 12px' }}>
            <button onClick={() => this.makeIndividualCalls()}>Make Individual Calls</button>
            <span style={{ marginLeft: '10px' }}>{this.state.lengthOfIndividualResponse + ' '} calls</span>
            <span style={{ marginLeft: '10px' }}>{this.state.timeForIndividualCalls + ' '} ms</span>
          </div>
        )}

        {this.shouldShowGetCollectiveCallsTest && (
          <div style={{ margin: '30px 12px' }}>
            <button onClick={() => this.makeCollectiveCall()}>Make Collective Call</button>
            <span style={{ marginLeft: '10px' }}>{this.state.lengthOfCollectiveResponse + ' '} ids</span>
            <span style={{ marginLeft: '10px' }}>{this.state.timeForCollectiveCall + ' '} ms</span>
          </div>
        )}

        {this.shouldShowGetQueryStringTest && (
          <div style={{ margin: '30px 12px' }}>
            <button onClick={() => this.testQueryString()}>Test Query String</button>
            <span style={{ marginLeft: '10px' }}>Query String: {' ' + this.state.queryString}</span>
          </div>
        )}

        {this.shouldShowGetCollectiveQueryStringTest && (
          <div style={{ margin: '30px 12px' }}>
            <button onClick={() => this.makeCollectiveCall2()}>Make Collective Call w/ queryStr</button>
            <span style={{ marginLeft: '10px' }}>{this.state.lengthOfCollectiveResponse2 + ' '} ids</span>
            <span style={{ marginLeft: '10px' }}>{this.state.timeForCollectiveCall2 + ' '} ms</span>
          </div>
        )}

        {this.shouldShowDatePicker2 && (
          <div className={classes.dateTimePanel}>
            <KDatePicker
              label="Start Date"
              value={this.state.controlledDate}
              onChange={(newValue) => this.handleNewDate(newValue)}
            />
            <KDatePicker
              label="2nd DatePicker"
              value={this.state.controlledDate}
              onChange={(newValue) => this.handleNewDate(newValue)}
            />
          </div>
        )}

        {this.shouldShowColumnChooser && (
          <ColumnChooser
            title="Select Columns"
            labels={['label1', 'label20000']}
            onToggle={(label, checked) => console.log(`tile ${label} is now ${checked}`)}
          />
        )}

        {this.shouldShowPlainTextField && (
          <div style={{ marginTop: '12px' }}>
            <TextField variant="filled" label="Test Me" InputLabelProps={{ color: 'primary' }} />
          </div>
        )}

        {this.shouldShowReorderableListTest && (
          <div style={{ marginTop: '12px' }}>
            <KReorderableList
              id="reorderList"
              addable={true}
              items={itemsToReorder}
              onSave={() => alert('should save now')}
            />
          </div>
        )}
      </div>
    );
  }
}

const itemsToReorder = [
  { id: 0, text: 'Apple', deletable: true },
  { id: 0, text: 'Cilantro', deletable: true },
  { id: 0, text: 'Kiwi', deletable: false },
];

const LocalMenuList = ({ items }) => {
  return (
    <MenuList>
      {items.map((item) => (
        <MenuItem key={item.name}>
          <FormControlLabel
            control={<Checkbox checked={item.checked} onChange={item.toggleFunc} value={item.name} color="primary" />}
            label={item.label}
          />
        </MenuItem>
      ))}
    </MenuList>
  );
};

const knownFirmsJson = `
{
  "knownFirms": [
    {
      "id": 2144,
      "name": "Maple Mountain Recovery",
      "firmType": "Treatment Center",
      "description": "Maple Mountain is a lovely facility with a sterling reputation.  Generous with referrals, too.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3496,
          "name": "Dorothea Dix"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2145,
      "name": "Sobertec",
      "firmType": "Treatment Center",
      "description": "Sobertec prides themselves on rapid througput.  They respond to email and voicemail, but not phone calls.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3497,
          "name": "Clara Barton"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2146,
      "name": "The Clearing",
      "firmType": "Treatment Center",
      "description": "The Clearing is totally new-agey.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3498,
          "name": "Mary Breckinridge"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2147,
      "name": "Queen of the Angels",
      "firmType": "Hospital",
      "description": "Standard hospital, serving full range of patients.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3499,
          "name": "Harriet Tubman"
        },
        {
          "id": 3546,
          "name": "Lance Et"
        },
        {
          "id": 3547,
          "name": "Cindy Rella"
        }
      ],
      "referralFromIds": [
        1340
      ],
      "webLinks": null
    },
    {
      "id": 2148,
      "name": "California Hospital",
      "firmType": "Hospital",
      "description": "Center City LA, but very nice facility.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3500,
          "name": "Jean d'Arc"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2149,
      "name": "Lead Generator",
      "firmType": "Treatment Center",
      "description": "A Testable Firm.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3492,
          "name": "Source1 Contact"
        },
        {
          "id": 3493,
          "name": "Target1 Contact"
        },
        {
          "id": 3494,
          "name": "Source2 Contact"
        },
        {
          "id": 3495,
          "name": "Target2 Contact"
        }
      ],
      "referralFromIds": [
        1316,
        1317
      ],
      "webLinks": null
    },
    {
      "id": 2150,
      "name": "Ashley Treatment",
      "firmType": "Treatment Center",
      "description": "In-patient Treatment Provider",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3485,
          "name": "Alex Denstman"
        },
        {
          "id": 3564,
          "name": "Second Contact"
        },
        {
          "id": 3565,
          "name": "Third Contact"
        },
        {
          "id": 3566,
          "name": "Aaron Kovac"
        },
        {
          "id": 3567,
          "name": "Andrew Karnak"
        },
        {
          "id": 3568,
          "name": "Bonnie Snyder"
        },
        {
          "id": 3569,
          "name": "Karli Samuelson"
        },
        {
          "id": 3570,
          "name": "DeeDee Hooper"
        },
        {
          "id": 3571,
          "name": "Ella May"
        },
        {
          "id": 3572,
          "name": "Fred Flintstone"
        },
        {
          "id": 3573,
          "name": "Candy Striper"
        },
        {
          "id": 3574,
          "name": "Dan deLion"
        },
        {
          "id": 3575,
          "name": "Gary Oldman"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2151,
      "name": "Caron Treatment Centers",
      "firmType": "Treatment Center",
      "description": "",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3486,
          "name": "James Flentje"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2152,
      "name": "Michael Avenati's Practice",
      "firmType": "Private Practitioner",
      "description": "Very aggressive professional.  May be inclined to get out over his skis.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3501,
          "name": "Michael Avenati"
        }
      ],
      "referralFromIds": [
        1321,
        1322
      ],
      "webLinks": null
    },
    {
      "id": 2153,
      "name": "Rudi Valentino's Practice",
      "firmType": "Private Practitioner",
      "description": "Seems to be an early pop icon, and a sex symbol of the 1920s.  Back from the dead to counsel the rising generation.",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3502,
          "name": "Rudi Valentino"
        }
      ],
      "referralFromIds": [
        1729
      ],
      "webLinks": null
    },
    {
      "id": 2165,
      "name": "L'Hopital Nuveau",
      "firmType": "Hospital",
      "description": "",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3548,
          "name": "Lola Lala"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2143,
      "name": "Test Firm",
      "firmType": null,
      "description": "",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3537,
          "name": "Ari Stottle"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2166,
      "name": "My Favorite Referral Company",
      "firmType": "Treatment Center",
      "description": "Very unselfish",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3578,
          "name": "Sue Mee"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2168,
      "name": "Ascension Recovery",
      "firmType": "Treatment Center",
      "description": "Treatment Center",
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 3591,
          "name": "Lauren McFall"
        }
      ],
      "referralFromIds": [],
      "webLinks": null
    },
    {
      "id": 2543,
      "name": "Privata Schrink's Practice",
      "firmType": "Private Practitioner",
      "description": null,
      "officePhone": null,
      "address": {
        "addressId": 0,
        "line1": null,
        "line2": null,
        "city": null,
        "state": null,
        "zip": null
      },
      "contacts": [
        {
          "id": 4487,
          "name": "Privata Schrink"
        }
      ],
      "referralFromIds": [
        1730
      ],
      "webLinks": null
    }
  ]
}
`;

const mapStateToProps = (state) => ({
  userInfo: state.userInfo,
});

const actionProducers = { setApiResponseInfo };

export default connect(mapStateToProps, actionProducers)(withStyles(styles)(AllevaPlayPage));
